import { useEffect, useState } from "react";
import { toast } from "sonner";
import { History, Plus, X, AlertCircle } from "lucide-react";
import { api, formatApiError, brl, todayStr } from "@/lib/api";

const apptServiceItem = (appointment, services) => {
  if (!appointment?.service) return null;
  const list = services || [];
  const svc = list.find((s) => s.id === appointment.service_id) || list.find((s) => s.name === appointment.service);
  return { type: "servico", name: appointment.service, price: svc ? svc.price || 0 : 0 };
};

export const SaleDialog = ({ saleId, sale: saleProp, client, appointment, services, onClose, onSaved }) => {
  const [saleData, setSaleData] = useState(saleProp || null);
  const [items, setItems] = useState(() => {
    if (saleProp) return saleProp.items;
    const it = apptServiceItem(appointment, services);
    return it ? [it] : [];
  });
  const [loaded, setLoaded] = useState(!saleId || Boolean(saleProp));
  const [editingIdx, setEditingIdx] = useState(null);
  const [newItem, setNewItem] = useState({ name: "", price: "" });
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [debt, setDebt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [discount, setDiscount] = useState(saleProp?.discount ? String(saleProp.discount) : "");

  useEffect(() => {
    if (saleId && !saleProp) {
      api
        .get(`/sales/${saleId}`)
        .then((res) => {
          setSaleData(res.data);
          setItems(res.data.items || []);
          setDiscount(res.data.discount ? String(res.data.discount) : "");
          setLoaded(true);
        })
        .catch(() => setLoaded(true));
    }
  }, [saleId]);

  const sale = saleData;
  const clientId = client?.id || appointment?.client_id || sale?.client_id || null;
  const clientName = client?.name || appointment?.client_name || sale?.client_name || "";

  useEffect(() => {
    if (!clientId) return;
    api
      .get(`/clients/${clientId}/history`)
      .then((res) => {
        setHistory(res.data);
        setDebt(res.data.debt_total || 0);
      })
      .catch(() => {});
  }, [clientId]);

  const startEdit = (idx) => {
    const it = items[idx];
    setEditingIdx(idx);
    setNewItem({ type: it.type, name: it.name, price: String(it.price) });
  };

  const pendingDebts = (history?.sales || []).filter((s) => s.status === "fiado" && !s.paid);
  const pendingTotal = pendingDebts.reduce((s, d) => s + d.total, 0);
  const debtsIncluded = items.some((i) => i._debtId);

  const includeDebts = () => {
    if (!pendingDebts.length) return;
    if (debtsIncluded) {
      toast.error("As dívidas já foram incluídas nesta comanda");
      return;
    }
    const debtItems = pendingDebts.map((s) => {
      const d = new Date(s.sale_date + "T12:00:00").toLocaleDateString("pt-BR");
      return { type: "servico", name: `Fiado ${d} — ${s.items.map((i) => i.name).join(", ")}`, price: s.total, _debtId: s.id };
    });
    setItems([...items, ...debtItems]);
    toast.success(`Dívida de ${brl(pendingTotal)} incluída na comanda`);
  };

  const applyEdit = () => {
    const copy = [...items];
    copy[editingIdx] = { type: "servico", name: newItem.name, price: Number(newItem.price) || 0 };
    setItems(copy);
    setEditingIdx(null);
    setNewItem({ type: "servico", name: "", price: "" });
  };

  const addItem = () => {
    if (!newItem.name.trim()) return;
    setItems([...items, { type: "servico", name: newItem.name.trim(), price: Number(newItem.price) || 0 }]);
    setNewItem({ type: "servico", name: "", price: "" });
  };

  const saveSale = async (status) => {
    if (!items.length) {
      toast.error("Adicione pelo menos um item à comanda");
      return;
    }
    setBusy(true);
    const payload = {
      client_id: clientId,
      client_name: clientName,
      sale_date: todayStr(),
      items,
      status,
      appointment_id: appointment?.id || sale?.appointment_id || null,
      discount: discountValue,
    };
    try {
      if (sale) await api.put(`/sales/${sale.id}`, payload);
      else await api.post("/sales", payload);
      if (status === "paga") {
        const debtIds = items.filter((i) => i._debtId).map((i) => i._debtId);
        if (debtIds.length) await api.post("/sales/receive", { ids: debtIds });
      }
      toast.success(status === "fiado" ? "Venda registrada como fiado" : "Venda recebida com sucesso!");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const subtotal = items.reduce((s, i) => s + (Number(i.price) || 0), 0);
  const discountValue = Math.max(0, Math.min(Number(discount) || 0, subtotal));
  const total = Math.max(0, subtotal - discountValue);

  const Input = (p) => (
    <input
      {...p}
      className={`w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:border-[#C8A279] focus:ring-2 focus:ring-[#C8A279]/20 transition-all ${p.className || ""}`}
    />
  );

  if (!loaded) {
    return (
      <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50" data-testid="sale-dialog">
        <div className="bg-white rounded-xl px-8 py-6 font-display text-xl text-stone-600">Carregando comanda…</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/50" data-testid="sale-dialog">
      <div className="bg-[#FAF9F5] rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto thin-scroll shadow-2xl">
        <div className="sticky top-0 bg-[#FAF9F5]/95 backdrop-blur border-b border-stone-200 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#C8A279]">Comanda</p>
            <p className="font-display text-2xl" data-testid="sale-client-name">{clientName}</p>
          </div>
          <div className="flex items-center gap-2">
            {debt > 0 && (
              <button
                data-testid="comanda-debt-indicator"
                onClick={() => setShowHistory(true)}
                className="flex items-center gap-2 bg-red-600 text-white text-xs font-semibold px-3 py-2 rounded-full hover:bg-red-700 transition-colors"
                title="Cliente possui dívida pendente — clique para ver o histórico"
              >
                <AlertCircle size={14} /> Dívida: {brl(debt)}
              </button>
            )}
            {pendingDebts.length > 0 && (
              <button
                data-testid="comanda-include-debt-button"
                onClick={includeDebts}
                disabled={debtsIncluded}
                className={`flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-full transition-colors ${
                  debtsIncluded
                    ? "bg-stone-200 text-stone-500 cursor-default"
                    : "bg-[#C8A279] text-[#121212] hover:bg-[#B38F68]"
                }`}
                title="Adiciona as dívidas fiado pendentes à comanda — ao Receber, elas são quitadas"
              >
                <Plus size={14} />
                {debtsIncluded ? "Dívidas já incluídas" : `Incluir dívidas (${brl(pendingTotal)})`}
              </button>
            )}
            {clientId && (
              <button
                data-testid="comanda-history-button"
                onClick={() => setShowHistory(true)}
                className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border border-stone-300 text-stone-600 hover:border-stone-900 transition-colors"
              >
                <History size={14} /> Histórico
              </button>
            )}
            <button onClick={onClose} data-testid="comanda-close-button" className="p-2 rounded-full hover:bg-stone-200 transition-colors" aria-label="Fechar">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="p-6 pb-24 space-y-3">
          {/* Existing items */}
          {items.map((it, idx) =>
            editingIdx === idx ? (
              <div key={idx} className="bg-white border border-[#C8A279] rounded-lg p-3 grid grid-cols-12 gap-2 items-center">
                <Input className="col-span-9" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} data-testid="sale-item-edit-name" />
                <Input className="col-span-2" type="number" step="0.01" value={newItem.price} onChange={(e) => setNewItem({ ...newItem, price: e.target.value })} data-testid="sale-item-edit-price" />
                <button onClick={applyEdit} className="col-span-1 text-sm text-green-600 hover:text-green-700 font-semibold" data-testid="sale-item-confirm-edit">✓</button>
              </div>
            ) : (
              <div
                key={idx}
                onContextMenu={(e) => {
                  e.preventDefault();
                  const del = window.confirm(`Excluir "${it.name}" da comanda?`);
                  if (del) setItems(items.filter((_, i) => i !== idx));
                  else startEdit(idx);
                }}
                className="bg-white border border-stone-200 rounded-lg px-4 py-3 flex items-center justify-between hover:border-stone-400 transition-colors"
                data-testid={`sale-item-row-${idx}`}
                title="Clique direito: alterar ou excluir"
              >
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-[#C8A279]">{it.type}</span>
                  <span className="text-sm font-medium">{it.name}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-semibold" data-testid={`sale-item-price-${idx}`}>{brl(it.price)}</span>
                  <button onClick={() => startEdit(idx)} className="text-xs text-stone-400 hover:text-stone-800 underline" data-testid={`sale-item-edit-${idx}`}>alterar</button>
                  <button onClick={() => setItems(items.filter((_, i) => i !== idx))} className="text-xs text-red-400 hover:text-red-600 underline" data-testid={`sale-item-delete-${idx}`}>excluir</button>
                </div>
              </div>
            )
          )}

          {/* Add item */}
          <div className="bg-white border border-dashed border-stone-300 rounded-lg p-3 grid grid-cols-12 gap-2 items-center">
            <Input
              className="col-span-8"
              list="sale-services-list"
              placeholder="Serviço…"
              value={newItem.name}
              onChange={(e) => {
                const svc = services?.find((s) => s.name === e.target.value);
                setNewItem({ ...newItem, name: e.target.value, price: svc ? String(svc.price) : newItem.price });
              }}
              data-testid="sale-new-item-name"
            />
            <datalist id="sale-services-list">
              {(services || []).map((s) => (
                <option key={s.id} value={s.name} />
              ))}
            </datalist>
            <Input className="col-span-2" type="number" step="0.01" placeholder="R$" value={newItem.price} onChange={(e) => setNewItem({ ...newItem, price: e.target.value })} data-testid="sale-new-item-price" />
            <button onClick={addItem} className="col-span-2 inline-flex items-center justify-center gap-1 text-xs font-semibold bg-[#121212] text-white rounded-md py-2 hover:bg-stone-700 transition-colors" data-testid="sale-add-item-button">
              <Plus size={13} /> Incluir
            </button>
          </div>

          <div className="flex items-center justify-between px-1 pt-2">
            <p className="text-sm text-stone-500">Itens: {items.length}</p>
            <div className="flex flex-col items-end gap-1">
              <p className="text-sm text-stone-500">Subtotal: {brl(subtotal)}</p>
              <div className="flex items-center gap-2">
                <label className="text-sm text-stone-500" htmlFor="sale-discount">Desconto:</label>
                <input
                  id="sale-discount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0,00"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  className="w-24 rounded-lg border border-stone-300 px-2.5 py-1.5 text-sm text-right focus:outline-none focus:border-[#C8A279] focus:ring-2 focus:ring-[#C8A279]/20 transition-all"
                  data-testid="sale-discount-input"
                />
              </div>
              <p className="font-display text-3xl" data-testid="sale-total">Total: {brl(total)}</p>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-[#FAF9F5]/95 backdrop-blur border-t border-stone-200 px-6 py-4 flex flex-wrap gap-3 justify-end">
          <button onClick={onClose} data-testid="comanda-cancel-button" className="px-6 py-3 rounded-full border border-stone-300 text-sm text-stone-600 hover:border-stone-900 transition-colors">
            Cancelar
          </button>
          <button
            onClick={() => saveSale("fiado")}
            disabled={busy}
            data-testid="comanda-fiado-button"
            className="px-6 py-3 rounded-full bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 transition-colors disabled:opacity-50"
          >
            Fiado
          </button>
          <button
            onClick={() => saveSale("paga")}
            disabled={busy}
            data-testid="comanda-receber-button"
            className="px-8 py-3 rounded-full bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            Receber
          </button>
        </div>

        {/* History modal */}
        {showHistory && history && (
          <div className="fixed inset-0 z-[160] flex items-center justify-center p-4 bg-black/40" onClick={() => setShowHistory(false)}>
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto thin-scroll p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="client-history-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-2xl">Histórico de {history.client.name}</h3>
                <button onClick={() => setShowHistory(false)} className="p-1.5 rounded-full hover:bg-stone-100"><X size={16} /></button>
              </div>
              {debt > 0 && (
                <p className="mb-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-2.5" data-testid="history-debt-amount">
                  Dívida pendente (fiado): <strong>{brl(debt)}</strong>
                </p>
              )}
              <div className="space-y-2">
                {history.sales.length === 0 && <p className="text-sm text-stone-400">Nenhuma venda registrada ainda.</p>}
                {history.sales.map((s) => (
                  <div key={s.id} className="flex items-center justify-between border border-stone-100 rounded-lg px-4 py-2.5 text-sm">
                    <div>
                      <p className="font-medium">{s.items.map((i) => i.name).join(", ")}</p>
                      <p className="text-xs text-stone-400">{new Date(s.sale_date + "T12:00:00").toLocaleDateString("pt-BR")} · {s.status}{s.status === "fiado" && !s.paid ? " (pendente)" : ""}</p>
                    </div>
                    <p className="font-semibold">{brl(s.total)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
